import { KnownOrderDtoStatus, OrderDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Snackbar } from '../../../ui/components/pure/snackbar/snackbar.component';
import { useOrderDetails } from './hooks/use-order-details.hook';
import { orderStatusToLabel } from '../orders.constants';
import { CancelOrderModal } from './actions/cancel-order/cancel-order-action.component';
import { OpenLockerModal } from './actions/open-locker/open-locker-modal.component';
import { OrderDetailsActions } from './actions/order-details.actions';
import { ResendNotificationModal } from '../../../ui/components/higher-order/resend-notification-action/resend-notification-modal.component';
import { UndoCancellationModal } from './actions/undo-cancellation/undo-cancellation-action.component';
import { MarkCollectedModal } from './actions/mark-collected/mark-collected-action.component';
import { RequestRetrievalModal } from './actions/request-retrieval/request-retrieval-action.component';
import { MarkCancelledModal } from './actions/mark-cancelled/mark-cancelled-action.component';
import { MarkRetrievedModal } from './actions/mark-retrieved/mark-retrieved-action.component';
import { useHistory } from 'react-router';
import { createPath, Paths } from '../../../app/router/app.router';
import { ArchiveOrderModal } from './actions/archive-order/archive-order-action.component';
import { DeliveryActionModal } from './actions/delivery/delivery-action.component';
import { LocationState } from '../../../ui/components/pure/list-table/list-table.types';
import { AddNoteActionModal } from '../../../ui/components/higher-order/add-note-action/add-note-modal.component';
import { KnownNoteEntityTypes } from '../../../ui/components/higher-order/add-note-action/add-note.hook';
import { SwitchSpaceOrderActionModal } from './actions/switch-space/switch-space-order-action.component';

export function OrderDetailsActionsObserver({
  order,
  state,
  handlers,
  retailerID,
  locationState
}: {
  order: OrderDto;
  state: ReturnType<typeof useOrderDetails>['state'];
  handlers: ReturnType<typeof useOrderDetails>['handlers'];
  retailerID: string;
  locationState: LocationState;
}) {
  const history = useHistory();
  const url = createPath(Paths.orders, { projectID: retailerID });

  switch (state.currentAction) {
    case OrderDetailsActions.ADD_NOTE:
      return (
        <AddNoteActionModal
          id={order.id}
          projectID={order.retailer.id}
          entityType={KnownNoteEntityTypes.Order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(OrderDetailsActions.ADD_NOTE_SUCCESS)}
        />
      );
    case OrderDetailsActions.ADD_NOTE_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          Note added
        </Snackbar>
      );
    case OrderDetailsActions.RESEND_NOTIFICATION:
      return (
        <ResendNotificationModal
          delivery={order}
          onSuccess={() => handlers.endAction(OrderDetailsActions.RESEND_NOTIFICATION_SUCCESS)}
          onAbort={() => handlers.endAction()}
        />
      );
    case OrderDetailsActions.SWITCH_SPACE:
      return <SwitchSpaceOrderActionModal order={order} onAbort={() => handlers.endAction()} />;
    case OrderDetailsActions.RESEND_NOTIFICATION_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          A notification resend request has been sent. The status of the notification will be available in the timeline
          shortly.
        </Snackbar>
      );
    case OrderDetailsActions.OPEN_LOCKER:
      return (
        <OpenLockerModal
          order={order}
          onSuccess={(statusChanged: boolean) => {
            handlers.endAction(statusChanged ? OrderDetailsActions.OPEN_LOCKER_SUCCESS : undefined);
          }}
          onAbort={() => handlers.endAction()}
        />
      );
    case OrderDetailsActions.OPEN_LOCKER_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The order has been marked {orderStatusToLabel[order.status as KnownOrderDtoStatus]}
        </Snackbar>
      );
    case OrderDetailsActions.CANCEL_ORDER:
      return (
        <CancelOrderModal
          order={order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => {
            handlers.endAction(OrderDetailsActions.CANCEL_ORDER_SUCCESS);
          }}
        />
      );
    case OrderDetailsActions.CANCEL_ORDER_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The order has been marked {orderStatusToLabel[order.status as KnownOrderDtoStatus]}
        </Snackbar>
      );
    case OrderDetailsActions.UNDO_CANCELLATION:
      return (
        <UndoCancellationModal
          order={order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(OrderDetailsActions.UNDO_CANCELLATION_SUCCESS)}
        />
      );
    case OrderDetailsActions.UNDO_CANCELLATION_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The order has been moved back to Booked status
        </Snackbar>
      );
    case OrderDetailsActions.MARK_COLLECTED:
      return (
        <MarkCollectedModal
          order={order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(OrderDetailsActions.MARK_COLLECTED_SUCCESS)}
        />
      );
    case OrderDetailsActions.MARK_COLLECTED_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The order has been marked as Collected
        </Snackbar>
      );
    case OrderDetailsActions.MARK_CANCELLED:
      return (
        <MarkCancelledModal
          order={order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(OrderDetailsActions.MARK_CANCELLED_SUCCESS)}
        />
      );
    case OrderDetailsActions.MARK_CANCELLED_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The order has been marked as {orderStatusToLabel[order.status as KnownOrderDtoStatus]}
        </Snackbar>
      );
    case OrderDetailsActions.REQUEST_RETRIEVAL:
      return (
        <RequestRetrievalModal
          order={order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(OrderDetailsActions.REQUEST_RETRIEVAL_SUCCESS)}
          onFailed={() => handlers.endAction(OrderDetailsActions.REQUEST_RETRIEVAL_FAILED)}
        />
      );
    case OrderDetailsActions.REQUEST_RETRIEVAL_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The order has been marked as {orderStatusToLabel[order.status as KnownOrderDtoStatus]}
        </Snackbar>
      );
    case OrderDetailsActions.REQUEST_RETRIEVAL_FAILED:
      return (
        <Snackbar severity="error" onClose={() => handlers.endAction()}>
          We can’t confirm the update right now. Please check back shortly and you’ll see the order updated.
        </Snackbar>
      );
    case OrderDetailsActions.MARK_RETRIEVED:
      return (
        <MarkRetrievedModal
          order={order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(OrderDetailsActions.MARK_RETRIEVED_SUCCESS)}
        />
      );
    case OrderDetailsActions.MARK_RETRIEVED_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The order has been marked as {orderStatusToLabel[order.status as KnownOrderDtoStatus]}
        </Snackbar>
      );
    case OrderDetailsActions.ARCHIVE_ORDER:
      return (
        <ArchiveOrderModal
          order={order}
          onAbort={() => handlers.endAction()}
          onSuccess={() => {
            handlers.endAction();
            history.push(`${url}${locationState?.from?.search || ''}`);
          }}
        />
      );
    case OrderDetailsActions.PORTAL_HANDOFF:
      return <DeliveryActionModal order={order} onComplete={handlers.endAction} onAbort={handlers.endAction} />;
    default:
      return null;
  }
}
